import { useCallback, useEffect, useState } from 'react';

import { ApiError, api } from '../../lib/api.js';
import { social } from './paths.js';
import type { Person } from './ShareDialog.js';

interface Invitation {
  projectId: number;
  projectName: string;
  from: Person;
  invitedAt: string;
}

/** Project invitations waiting for an answer: nothing opens until you accept. */
export function Invitations({ onAccepted }: { onAccepted: () => void }) {
  const [items, setItems] = useState<Invitation[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      setItems((await api.get<{ invitations: Invitation[] }>(social('/invitations'))).invitations);
    } catch {
      // The list is simply empty when it cannot be loaded.
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 30_000);
    return () => window.clearInterval(timer);
  }, [load]);

  async function answer(inv: Invitation, accept: boolean) {
    setBusy(inv.projectId);
    setError(null);
    try {
      await api.post(social(`/invitations/${inv.projectId}/${accept ? 'accept' : 'decline'}`));
      await load();
      if (accept) onAccepted();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not answer that invitation.');
      await load();
    } finally {
      setBusy(null);
    }
  }

  if (items.length === 0 && !error) return null;
  return (
    <section className="soc-invites">
      <h2>Invitations</h2>
      {error && <p className="login-error">{error}</p>}
      <ul className="site-list">
        {items.map((inv) => (
          <li key={inv.projectId}>
            <span className="site-list-main">
              <strong>{inv.projectName}</strong>
              <span className="muted">
                <strong>{inv.from.username}</strong> invited you to work on this project together
              </span>
            </span>
            <span className="soc-actions">
              <button type="button" className="primary" disabled={busy !== null} onClick={() => void answer(inv, true)}>
                Accept
              </button>
              <button type="button" className="ghost" disabled={busy !== null} onClick={() => void answer(inv, false)}>
                Decline
              </button>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
